import type { IDietPlan, IUserProfile } from '@/hooks/useAppState';
import type { PlanDailyTargets, WorkoutMealSchedule } from '@/data/diet-plan-templates';
import { calcPlanDailyTargets, generatePlanMealFoods } from '@/data/diet-plan-templates';
import { attachedPlanIssue, isAttachedFatLoss } from '@/data/attached-fat-loss';
import { weeklyHoursAtDate } from './trainingScheduleHistory';
import type { IMealFood, MealType } from '@/data/foods';

export interface DietDaySnapshot {
  mealSchedule?: WorkoutMealSchedule;
  template: IDietPlan['planTemplate'];
  targets: PlanDailyTargets;
  dayIndex: number;
  carbDay: 'high' | 'medium' | 'low';
  carbDayLabel: string;
  carbDaySubLabel: string;
  isTrainingDay: boolean;
  mealFoods: Record<MealType, IMealFood[]>;
  capturedAt: number;
}
export const isRetiredFatLoss = (id: string) => ['three-month', 'new-year-40', 'triple-carb'].includes(id);
export function decodeDietPlanId(raw: string) {
  try { const parsed = JSON.parse(raw); if (typeof parsed === 'string') return parsed; } catch { /* legacy unquoted ID */ }
  return raw;
}
export function dietDayIndex(plan: Pick<IDietPlan, 'startDate' | 'planTemplate'>, date: Date) {
  if (isAttachedFatLoss(plan.planTemplate)) {
    const start = new Date(plan.startDate);
    const ordinal = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
    return Math.max(1, Math.floor((ordinal(date) - ordinal(start)) / 86400000) + 1);
  }
  return Math.max(1, Math.floor((date.getTime() - plan.startDate) / 86400000) + 1);
}
export function dietFallbackReason(plan: IDietPlan, isMember: boolean, date = new Date()) {
  if (plan.planTemplate === 'generic') return undefined;
  const dateKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  if (!isMember || plan.memberDietSuspended || (plan.memberAccessUntil && plan.memberAccessUntil < dateKey)) return 'expired' as const;
  if (isRetiredFatLoss(plan.planTemplate)) return 'retired' as const;
  const limit = plan.planTemplate === 'three-month-2026' ? 90 : plan.planTemplate === 'sprint-40-2026' ? 40 : null;
  if (limit && dietDayIndex(plan, date) > limit) return 'completed' as const;
  if (isAttachedFatLoss(plan.planTemplate) && attachedPlanIssue({ template: plan.planTemplate, weight: plan.weight, height: plan.height, age: plan.age, weeklyHours: weeklyHoursAtDate(plan, date) })) return 'reassessment' as const;
  return undefined;
}
export function effectiveDietPlan(plan: IDietPlan | null, profile: IUserProfile, isMember: boolean): IDietPlan | null {
  if (!plan) return null;
  const reason = dietFallbackReason(plan, isMember);
  if (!reason) return plan;
  const positive = (value: number | undefined, fallback: number) => Number.isFinite(value) && value! > 0 ? value! : fallback;
  const weight = positive(profile.weight, plan.weight), height = positive(profile.height, plan.height);
  const age = positive(profile.age, plan.age || 25), weeklyHours = positive(profile.weeklyTrainingHours, plan.weeklyHours);
  const gender = profile.gender === 'male' || profile.gender === 'female' ? profile.gender : plan.gender;
  const bodyType = profile.bodyType || plan.bodyType;
  const goal = ['fat-loss', 'muscle-gain', 'shape'].includes(profile.goal) ? profile.goal : plan.goal;
  const targets = calcPlanDailyTargets({ template: 'generic', weight, gender, age, height, weeklyHours, bodyType, goal });
  const foods = generatePlanMealFoods('generic', targets, gender);
  return { ...plan, planTemplate: 'generic', name: goal === 'muscle-gain' ? '通用增肌方案' : goal === 'shape' ? '通用塑形方案' : '通用减脂方案',
    goal, weight, height, gender, age, weeklyHours, bodyType,
    avoidFoods: Array.from(new Set([...(plan.avoidFoods || []), ...(profile.avoidFoods || []), ...(plan.allergens || []), ...(profile.allergens || [])])),
    supplements: [], mealFoods: foods, serviceNotice: reason };
}
export function findDietSnapshot(plans: IDietPlan[], dateKey: string): DietDaySnapshot | undefined {
  return plans.map(plan => plan.dailySnapshots?.[dateKey]).filter((value): value is DietDaySnapshot =>
    !!value && Number.isFinite(value.targets?.calories) && !!value.mealFoods && Number.isFinite(value.capturedAt))
    .sort((a, b) => a.capturedAt - b.capturedAt)[0];
}
export function historicalDietFoods(plans: IDietPlan[], dateKey: string) {
  const snapshot = findDietSnapshot(plans, dateKey);
  const edits = plans.reduce((result, plan) => ({ ...result, ...plan.dailyMealFoods?.[dateKey] }), {});
  return { ...snapshot?.mealFoods, ...edits } as Partial<Record<MealType, IMealFood[]>>;
}
export function normalizeDietPlans(plans: IDietPlan[]) {
  // Keep every plan and its historical dates, including duplicate templates.
  return plans.map(plan => ({ ...plan, planTemplate: plan.planTemplate || 'generic',
    startDate: plan.startDate || plan.createdAt || Date.now(),
    goal: (plan.goal as string) === 'maintain' ? 'shape' as const : plan.goal }));
}
